/**
 * React Query hooks for monitoring operations.
 * Provides declarative data fetching for sandbox operation statistics.
 * Three independent hooks enable incremental page rendering.
 *
 * @module hooks/useMonitoring
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { monitoringService } from '@/services/monitoringService';

/**
 * Query key factory for monitoring.
 * Ensures consistent cache key generation.
 */
export const monitoringKeys = {
  all: ['monitoring'] as const,
  summary: (startTime: string, endTime: string) =>
    [...monitoringKeys.all, 'summary', startTime, endTime] as const,
  timeSeries: (startTime: string, endTime: string) =>
    [...monitoringKeys.all, 'timeSeries', startTime, endTime] as const,
  failureDetails: (startTime: string, endTime: string) =>
    [...monitoringKeys.all, 'failureDetails', startTime, endTime] as const,
};

export function useMonitoringSummary(startTime: string, endTime: string) {
  return useQuery({
    queryKey: monitoringKeys.summary(startTime, endTime),
    queryFn: () => monitoringService.getSummary(startTime, endTime),
    enabled: !!startTime && !!endTime,
    staleTime: 5 * 60 * 1000,
  });
}

export function useMonitoringTimeSeries(startTime: string, endTime: string) {
  return useQuery({
    queryKey: monitoringKeys.timeSeries(startTime, endTime),
    queryFn: () => monitoringService.getTimeSeries(startTime, endTime),
    enabled: !!startTime && !!endTime,
    staleTime: 5 * 60 * 1000,
  });
}

export function useMonitoringFailureDetails(startTime: string, endTime: string) {
  return useQuery({
    queryKey: monitoringKeys.failureDetails(startTime, endTime),
    queryFn: () => monitoringService.getFailureDetails(startTime, endTime),
    enabled: !!startTime && !!endTime,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook that returns a function to invalidate all monitoring queries (for refresh button).
 */
export function useInvalidateMonitoring() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: monitoringKeys.all });
}
